import { ZViewSnapshotState, type IViewSnapshotState } from "@multiconsult-group/multiplacer-core";
import type { IAppThunk } from "../app/store";
import { openapi } from "../../openapi";
import { changeActiveModuleThunk } from "./designThunk";
import {
  setFloorAndJobInRedux,
  setRotationInRedux,
  setSelectedBuildingInRedux,
  setSelectedSpaceNumberInRedux,
} from "./designSlice";
import { restoreViewerCameraInRedux } from "../ViewerRedux/viewerSlice";
import { applyViewerViewThunk } from "./applyViewerViewThunk";
export { applyViewerViewThunk } from "./applyViewerViewThunk";

/** The caller opens the correct project before applying its shared snapshot. */
export const applySharedViewStateThunk = (state: IViewSnapshotState, signal?: AbortSignal): IAppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const snapshot = ZViewSnapshotState.parse(state);
    const projectNo = getState().design.projectNo;
    if (!projectNo) throw new Error("Select a project before applying a shared view");

    // Resolve real floor/job identifiers before changing any view state.
    const request = dispatch(openapi.endpoints.getMcbimBuildingStories.initiate({ projectNo }));
    try {
      const stories = await request.unwrap();
      if (signal?.aborted) return;
      if (getState().design.projectNo !== projectNo) {
        throw new Error("Project changed while loading the shared view");
      }
      const story = stories.find(story =>
        story.building?.trim() === snapshot.context.building && story.name === snapshot.context.floorName
      );
      if (!story) throw new Error(`Shared view floor not found: ${snapshot.context.building} / ${snapshot.context.floorName}`);
      // Module defaults must run first, otherwise they can overwrite the saved visibility.
      dispatch(changeActiveModuleThunk(snapshot.context.module));
      dispatch(setSelectedBuildingInRedux(story.building?.trim() || "Unknown"));
      dispatch(setFloorAndJobInRedux({
        building: story.building,
        floorName: story.name,
        floorGlobalId: story.globalId,
        jobId: story.job.jobId,
        elevation: story.elevation,
      }));
      dispatch(applyViewerViewThunk(snapshot.view));
      dispatch(setSelectedSpaceNumberInRedux(snapshot.context.selectedSpaceNumber));
      dispatch(setRotationInRedux(snapshot.context.camera.rotation));
      dispatch(restoreViewerCameraInRedux({ floorGlobalId: story.globalId, camera: snapshot.context.camera }));
    } finally {
      request.unsubscribe();
    }
  };
